import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Parent,
  ParentCreateInput,
  ParentListFilters,
  ParentListResponse,
  ParentStudentsResponse,
  ParentUpdateInput,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase J3 + J3.b — TanStack Query bindings for `/api/parents/*`.
 *
 * J3 scope: list + detail + students-for-parent (read-only).
 * J3.b extends with the two BE write mutations:
 *   - `useCreateParent` POST  /api/parents/
 *   - `useUpdateParent` PATCH /api/parents/{id}
 *
 * The parent surface has no soft-delete or status-transition endpoint
 * on the BE side; parent records live as long as the linked students
 * do (linked through `Student.parent_id`). When a parent record needs
 * to be removed in practice, the convention is to detach by clearing
 * `parent_id` on the students, then leave the orphaned parent record
 * for the household audit trail.
 *
 * Tenant scoping is identical to students — the api-client interceptor
 * injects `X-Tenant-Slug` from the active session; the bearer token
 * carries `tenant_id` for the BE guard. `view_students` is the read
 * guard (admin + teacher hold it by default); `manage_students` is
 * the write guard.
 */
export const parentKeys = {
  all: ["parents"] as const,
  list: (filters?: ParentListFilters) => [...parentKeys.all, "list", filters ?? {}] as const,
  detail: (id: string | null) => [...parentKeys.all, "detail", id] as const,
  students: (id: string | null) => [...parentKeys.all, "students", id] as const,
};

function buildParentsQuery(filters?: ParentListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.relationship) search.set("relationship", filters.relationship);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useParentsList(filters?: ParentListFilters) {
  const qs = buildParentsQuery(filters);
  return useQuery<ParentListResponse>({
    queryKey: parentKeys.list(filters),
    queryFn: () => api.get<ParentListResponse>(`/api/parents/${qs ? `?${qs}` : ""}`),
    staleTime: 15_000,
  });
}

export function useParent(parentId: string | null) {
  return useQuery<Parent>({
    queryKey: parentKeys.detail(parentId),
    queryFn: () => api.get<Parent>(`/api/parents/${parentId}`),
    enabled: Boolean(parentId),
    staleTime: 30_000,
  });
}

export function useParentStudents(parentId: string | null) {
  return useQuery<ParentStudentsResponse>({
    queryKey: parentKeys.students(parentId),
    queryFn: () => api.get<ParentStudentsResponse>(`/api/parents/${parentId}/students`),
    enabled: Boolean(parentId),
    staleTime: 30_000,
  });
}

/**
 * UI lookup for the relationship Select on filters / write form.
 * Mirrors `eduviaos-backend/app/domains/parents/schemas.py::RelationshipLiteral`.
 */
export const PARENT_RELATIONSHIPS = [
  { value: "father", label: "Father" },
  { value: "mother", label: "Mother" },
  { value: "guardian", label: "Guardian" },
  { value: "other", label: "Other" },
] as const;

// ---------------------------------------------------------------------------
// Phase J3.b — write mutations
// ---------------------------------------------------------------------------

export function useCreateParent() {
  const qc = useQueryClient();
  return useMutation<Parent, Error, ParentCreateInput>({
    mutationKey: ["parents", "create"],
    mutationFn: (body) => api.post<Parent>("/api/parents/", body),
    onSuccess: (parent) => {
      qc.setQueryData(parentKeys.detail(parent.id), parent);
      void qc.invalidateQueries({ queryKey: parentKeys.all });
    },
  });
}

export interface UseUpdateParentVars {
  id: string;
  body: ParentUpdateInput;
}

export function useUpdateParent() {
  const qc = useQueryClient();
  return useMutation<Parent, Error, UseUpdateParentVars>({
    mutationKey: ["parents", "update"],
    mutationFn: ({ id, body }) => api.patch<Parent>(`/api/parents/${id}`, body),
    onSuccess: (parent) => {
      qc.setQueryData(parentKeys.detail(parent.id), parent);
      void qc.invalidateQueries({ queryKey: parentKeys.all });
    },
  });
}
